/** Shared progressive enhancements for Alfresco Chapel. */
import { dispatchMessage } from './ac-dispatch.js';
class DispatchCountdown extends HTMLElement {
  connectedCallback(){this.update();this.timer=setInterval(()=>this.update(),60000);}
  disconnectedCallback(){clearInterval(this.timer);}
  update(){const value=dispatchMessage(new Date(),this.dataset.cutoff,(this.dataset.holidays||'').split(',').map(x=>x.trim()));this.hidden=!value;if(value)this.textContent=value;}
}
if(!customElements.get('ac-dispatch-countdown'))customElements.define('ac-dispatch-countdown',DispatchCountdown);

import { CartLinesUpdateEvent } from '@shopify/events';

class FrequentlyBoughtTogether extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.addEventListener('change',()=>this.update(),{signal:this.abort.signal});
    this.querySelector('[data-fbt-submit]')?.addEventListener('click',()=>this.add(),{signal:this.abort.signal});
    this.update();
  }
  disconnectedCallback(){this.abort?.abort();}
  selected(){return [...this.querySelectorAll('input[type=checkbox]:checked')];}
  update(){
    const selected=this.selected();
    this.querySelector('[data-fbt-count]').textContent=`${selected.length} ${this.dataset.selectedLabel}`;
    this.querySelector('[data-fbt-total]').textContent=new Intl.NumberFormat(document.documentElement.lang,{style:'currency',currency:this.dataset.currency||'GBP'}).format(selected.reduce((sum,input)=>sum+Number(input.dataset.price),0)/100);
    this.querySelector('[data-fbt-submit]').disabled=this.busy||selected.length===0;
  }
  async add(){
    if(this.busy)return;
    const selected=this.selected();if(!selected.length)return;
    this.busy=true;this.update();
    const status=this.querySelector('[data-fbt-status]');status.textContent='';
    const items=selected.map(input=>({id:Number(input.value),quantity:1}));
    const deferred=CartLinesUpdateEvent.createPromise();
    deferred.promise.catch(()=>{});
    this.dispatchEvent(new CartLinesUpdateEvent({action:'add',context:'product',lines:items.map(i=>({merchandiseId:String(i.id),quantity:i.quantity})),promise:deferred.promise}));
    try {
      const root=window.Shopify?.routes?.root||'/';
      const sections=[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(x=>x.dataset.sectionId))].slice(0,5);
      const response=await fetch(`${root}cart/add.js`,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections,sections_url:location.pathname})});
      const result=await response.json();if(!response.ok||result.status)throw new Error(result.description||'Unable to add the selected products.');
      const cartResponse=await fetch(`${root}cart.js`);if(!cartResponse.ok)throw new Error('Products were added. Refresh the page to view your updated cart.');
      const cart=await cartResponse.json();
      deferred.resolve({cart:CartLinesUpdateEvent.createCartFromAjaxResponse(cart),detail:{items:cart.items,sections:result.sections,source:'ac-fbt',didError:false}});
      status.textContent='Added to your cart.';
    } catch(error){deferred.reject(error);status.textContent=error.message;}
    finally{this.busy=false;this.update();}
  }
}
if(!customElements.get('ac-fbt'))customElements.define('ac-fbt',FrequentlyBoughtTogether);

let reviewsLoader;
function loadReviews(){
  if(typeof window.ReviewsWidget==='function')return Promise.resolve();
  if(reviewsLoader)return reviewsLoader;
  reviewsLoader=new Promise((resolve,reject)=>{
    let script=document.querySelector('script[src*="widget.reviews.co.uk/polaris/build.js"]');
    const timer=setTimeout(()=>reject(new Error('Reviews could not be loaded')),15000);
    const done=()=>{clearTimeout(timer);typeof window.ReviewsWidget==='function'?resolve():reject(new Error('Reviews unavailable'));};
    if(!script){script=document.createElement('script');script.src='https://widget.reviews.co.uk/polaris/build.js';script.async=true;script.addEventListener('load',done,{once:true});script.addEventListener('error',()=>{clearTimeout(timer);reject(new Error('Reviews unavailable'));},{once:true});document.head.append(script);}
    else{script.addEventListener('load',done,{once:true});if(typeof window.ReviewsWidget==='function')done();}
  });
  return reviewsLoader;
}
class ProductReviews extends HTMLElement{
  connectedCallback(){this.observer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){this.observer.disconnect();this.mount();}},{rootMargin:'300px'});this.observer.observe(this);}
  disconnectedCallback(){this.observer?.disconnect();}
  async mount(){
    try{await loadReviews();if(!this.isConnected||this.dataset.mounted)return;this.dataset.mounted='true';
      const target=this.querySelector('.ac-review-widget');
      new window.ReviewsWidget(`#${target.id}`,{store:this.dataset.store,widget:'polaris',options:{types:'product_review,questions',lang:'en',per_page:4,product_review:{sku:this.dataset.skus,hide_if_no_results:false},questions:{grouping:this.dataset.skus,hide_if_no_results:false,enable_ask_question:true,show_dates:true},header:{enable_summary:true,enable_ratings:true,enable_write_review:true,enable_ask_question:true},reviews:{enable_verified_badge:true,enable_reviewer_name:true,enable_date:true,enable_images:true,enable_replies:true}},styles:{'--base-font-size':'14px','--common-button-font-family':'Hanken Grotesk, sans-serif','--heading-text-font-family':'Syne, sans-serif','--body-text-font-family':'Hanken Grotesk, sans-serif','--common-star-color':'#D9622B','--primary-button-bg-color':'#7A2E1E','--primary-button-border-color':'#7A2E1E','--primary-button-text-color':'#F5F1E9'}});
    }catch{this.querySelector('.ac-review-fallback')?.removeAttribute('hidden');}
  }
}
if(!customElements.get('ac-reviews'))customElements.define('ac-reviews',ProductReviews);

class StickyQuantity extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.section = this.closest('.shopify-section');
    this.addEventListener('click',event=>{
      const button=event.target.closest('button[data-step]');
      if(!button)return;
      this.source()?.querySelector(`[name="${button.dataset.step}"]`)?.click();
      this.sync();
    },{signal:this.abort.signal});
    document.addEventListener('quantity-selector:update',event=>{
      if(!event.detail.cartLine)this.sync();
    },{signal:this.abort.signal});
    this.observer=new MutationObserver(()=>this.sync());
    const details=this.section?.querySelector('.product-details');
    if(details)this.observer.observe(details,{childList:true,subtree:true,attributes:true,attributeFilter:['disabled','value','min','max']});
    this.sync();
  }
  disconnectedCallback(){this.abort?.abort();this.observer?.disconnect();}
  source(){return this.section?.querySelector('.product-details quantity-selector-component');}
  sync(){
    const source=this.source();this.hidden=!source;if(!source)return;
    this.querySelector('output').textContent=source.querySelector('input')?.value||'1';
    for(const step of ['minus','plus'])this.querySelector(`[data-step="${step}"]`).disabled=source.querySelector(`[name="${step}"]`)?.disabled??true;
  }
}
if(!customElements.get('ac-sticky-quantity'))customElements.define('ac-sticky-quantity',StickyQuantity);
